// Goal-setup prompt scheduler.
//
// We surface a "set goals for this month/year" card on the Dashboard the
// FIRST time the user opens the app in a new month (or year) when no
// goals exist for that period. Once they either set goals OR dismiss the
// prompt, we remember that for the rest of the period and don't bother
// them again.
//
// Best-practice timing rationale (BJ Fogg-style "fresh start effect"):
//   - Monthly prompt fires at the rollover of a new month, when planning
//     intent is naturally highest.
//   - Yearly prompt fires at the rollover of a new year, separated from
//     the monthly one (different localStorage key) so a single "set my
//     goals" mode doesn't conflate the two timeframes.
//
// We intentionally do NOT alarm-clock the user: the prompt is a quiet
// card on the dashboard, not a blocking modal. They can ignore it and
// dismiss when ready.

const MONTHLY_KEY = 'proxera.goalPrompt.monthly.v1';
const YEARLY_KEY  = 'proxera.goalPrompt.yearly.v1';

function read(key) {
  try { return localStorage.getItem(key) || null; } catch { return null; }
}
function write(key, value) {
  try { localStorage.setItem(key, value); } catch {}
}

// Period strings are produced by the Dashboard already:
//   monthly → 'YYYY-MM'   (e.g. '2026-05')
//   yearly  → 'YYYY'      (e.g. '2026')
// We just remember the most-recently-handled period; if it differs from
// the current one we know we're in a fresh window.

export function isMonthlyHandled(period) {
  return read(MONTHLY_KEY) === period;
}
export function markMonthlyHandled(period) {
  write(MONTHLY_KEY, period);
}

export function isYearlyHandled(period) {
  return read(YEARLY_KEY) === period;
}
export function markYearlyHandled(period) {
  write(YEARLY_KEY, period);
}

// "Should we show the prompt?" combines the period flag with the
// caller's current goal count so that even if the prompt was dismissed
// in error, it stops nagging once the user has saved at least one goal.
//
// We separate the monthly and yearly checks so a Dashboard render can
// pick whichever bucket is relevant to what the user is looking at.
export function shouldPromptMonthly({ period, goalCount }) {
  if (goalCount > 0) return false;
  return !isMonthlyHandled(period);
}
export function shouldPromptYearly({ period, goalCount }) {
  if (goalCount > 0) return false;
  return !isYearlyHandled(period);
}
